import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { VisualizationConfig } from "../types";

interface ChartRendererProps {
  config: VisualizationConfig;
  data: Record<string, unknown>[];
  /** Rows per table page. The inline chat card keeps the default; the side
   *  visualiser passes a larger value so the table fills its full height. */
  pageSize?: number;
}

const DEFAULT_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#06B6D4", "#F97316", "#84CC16",
];

// ── Column display helpers ───────────────────────────────────────────────────

const COLUMN_LABELS: Record<string, string> = {
  empID: "Emp ID",
  empName: "Employee Name",
  roName: "Regional Office",
  kvName: "School",
  schoolName: "School",
  attendance_status: "Status",
  attendanceDate: "Date",
  class_id: "Class",
  academic_year: "Year",
  student_id: "Student ID",
  kvID: "KV ID",
  roID: "RO ID",
  cognitive_score: "Cognitive",
  physical_dev_score: "Physical Dev",
  language_score: "Language",
  aesthetic_score: "Aesthetic",
  socio_emotional_score: "Socio-Emotional",
  learning_habits_score: "Learning Habits",
  confidence_score: "Confidence",
  focus_score: "Focus",
  peer_interaction_score: "Peer Interaction",
  motivation_score: "Motivation",
  leadership_score: "Leadership",
  participation_score: "Participation",
};

function colLabel(col: string): string {
  return (
    COLUMN_LABELS[col] ??
    col
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Aggregate columns produced by window functions — hidden from table when
// MetricCards is showing the totals above.
const AGGREGATE_COLS = new Set([
  "totalEnrolled", "totalPresent", "totalHalfDay", "totalAbsent",
]);

const STATUS_BADGE: Record<string, { cls: string; symbol: string }> = {
  Present:    { cls: "bg-green-100 text-green-700", symbol: "✓" },
  "Half Day": { cls: "bg-amber-100 text-amber-700", symbol: "◑" },
  Absent:     { cls: "bg-red-100 text-red-700",   symbol: "✗" },
};

function formatCell(value: unknown, col: string): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-300">—</span>;
  }

  // Attendance status badge
  if (col === "attendance_status") {
    const v = String(value);
    const b = STATUS_BADGE[v];
    if (b) {
      return (
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${b.cls}`}>
          <span>{b.symbol}</span>{v}
        </span>
      );
    }
    return v;
  }

  // Numbers
  if (typeof value === "number") {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toFixed(2);
  }

  // Numeric strings > 3 digits (e.g. "44861" from JDBC)
  if (typeof value === "string" && /^\d+$/.test(value) && value.length > 3) {
    return parseInt(value, 10).toLocaleString();
  }

  return String(value);
}

// ── DataTable ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 25;

const DataTable: React.FC<{ data: Record<string, unknown>[]; pageSize?: number }> = ({ data, pageSize }) => {
  const PAGE_SIZE = pageSize ?? DEFAULT_PAGE_SIZE;
  const [page, setPage]     = useState(0);
  const [search, setSearch] = useState("");

  // Reset page when search changes
  useEffect(() => { setPage(0); }, [search]);

  if (!data.length) return null;

  const allCols = Object.keys(data[0]);

  // Detect constant aggregate columns (same value across all sampled rows).
  // These are already shown in MetricCards — hide them from the table.
  const constantAggCols = new Set(
    data.length > 1
      ? allCols.filter((col) => {
          if (!AGGREGATE_COLS.has(col)) return false;
          const first = data[0][col];
          return data.slice(0, 100).every((row) => row[col] === first);
        })
      : []
  );

  const displayCols =
    constantAggCols.size > 0
      ? allCols.filter((c) => !constantAggCols.has(c))
      : allCols;

  // Search
  const q = search.trim().toLowerCase();
  const filtered = q
    ? data.filter((row) =>
        Object.values(row).some((v) =>
          String(v ?? "").toLowerCase().includes(q)
        )
      )
    : data;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);
  const visible    = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const startRow   = safePage * PAGE_SIZE + 1;

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg
              bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 w-44"
          />
        </div>

        <span className="text-xs text-gray-400">
          {q && filtered.length < data.length
            ? `${filtered.length.toLocaleString()} of ${data.length.toLocaleString()} rows`
            : `${data.length.toLocaleString()} rows`}
        </span>

        {totalPages > 1 && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-gray-400 mr-1">
              Page {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-100 max-h-[460px] overflow-y-auto">
        <table className="w-full text-xs text-left">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2.5 font-medium text-gray-400 w-10 text-right">#</th>
              {displayCols.map((c) => (
                <th key={c} className="px-3 py-2.5 font-semibold text-gray-600 whitespace-nowrap">
                  {colLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr
                key={startRow + i}
                className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors last:border-0"
              >
                <td className="px-3 py-2 text-gray-300 tabular-nums text-right">
                  {startRow + i}
                </td>
                {displayCols.map((c) => (
                  <td key={c} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                    {formatCell(row[c], c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Toggle buttons ───────────────────────────────────────────────────────────

const ViewToggle: React.FC<{
  mode: "chart" | "table";
  onChange: (m: "chart" | "table") => void;
}> = ({ mode, onChange }) => (
  <div className="flex rounded-lg overflow-hidden border border-gray-200">
    <button
      onClick={() => onChange("chart")}
      className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors
        ${mode === "chart"
          ? "bg-blue-50 text-blue-700 font-medium"
          : "bg-white text-gray-500 hover:bg-gray-50"}`}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      Chart
    </button>
    <button
      onClick={() => onChange("table")}
      className={`px-3 py-1.5 text-xs flex items-center gap-1 border-l border-gray-200 transition-colors
        ${mode === "table"
          ? "bg-blue-50 text-blue-700 font-medium"
          : "bg-white text-gray-500 hover:bg-gray-50"}`}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      Table
    </button>
  </div>
);

// ── Main ChartRenderer ───────────────────────────────────────────────────────

const ChartRenderer: React.FC<ChartRendererProps> = ({ config, data, pageSize }) => {
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  if (!data || data.length === 0) return null;

  const isTableType = config.type === "table";
  const showToggle  = !isTableType;

  if (isTableType || viewMode === "table") {
    return (
      <div className="w-full space-y-3">
        {(config.title || showToggle) && (
          <div className="flex items-center justify-between">
            {config.title
              ? <h3 className="text-sm font-semibold text-gray-700">{config.title}</h3>
              : <span />}
            {showToggle && (
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            )}
          </div>
        )}
        <DataTable data={data} pageSize={pageSize} />
      </div>
    );
  }

  // Chart view
  const chartProps = {
    data,
    margin: { top: 8, right: 24, left: 0, bottom: 8 },
  };

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between">
        {config.title
          ? <h3 className="text-sm font-semibold text-gray-700">{config.title}</h3>
          : <span />}
        <ViewToggle mode={viewMode} onChange={setViewMode} />
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {config.type === "bar" ? (
          <BarChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey={config.xAxis} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {config.series.map((s, i) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name}
                fill={s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                radius={[3, 3, 0, 0]}
              />
            ))}
          </BarChart>
        ) : config.type === "line" ? (
          <LineChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey={config.xAxis} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {config.series.map((s, i) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        ) : (
          <PieChart>
            <Pie
              data={data}
              dataKey={config.series[0]?.dataKey ?? "value"}
              nameKey={config.xAxis}
              cx="50%" cy="50%"
              outerRadius={120}
              label={(props: any) => {
                const name = props.name ?? (props.payload && props.payload[config.xAxis]) ?? "";
                const pct  = typeof props.percent === "number" ? props.percent : 0;
                return `${name} ${(pct * 100).toFixed(0)}%`;
              }}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default ChartRenderer;
